/**
 * **Kontrol baris terpilih** — menempel INLINE di bawah barisnya sendiri.
 *
 * ### ⭐ Kenapa inline, bukan panel di bawah daftar
 * Panel terpisah memaksa mata melompat: baris di atas, tombolnya di bawah, totalnya di bawah lagi.
 * Inline menaruh jumlah tepat di tempat angka jumlah itu terbaca — jarak tempuh mata nol, dan
 * total tetap terlihat sepanjang penyuntingan.
 *
 * ⚠️ Ia **memekarkan** barisnya, bukan melayang di atasnya. Panel melayang menutupi baris lain
 * persis saat kasir membandingkan dua baris yang mirip.
 *
 * ### ⛔ Kenapa `Hapus` TERPISAH dari `−`
 * Sebelumnya satu tombol `−` mengurangi jumlah **dan** menghapus baris saat jumlahnya tinggal 1.
 * Artinya satu ketukan kelebihan menghapus baris tanpa peringatan — dan di jam sibuk, ketukan
 * kelebihan adalah keadaan normal. Sekarang `−` berhenti di 1; menghapus adalah keputusan sendiri.
 */
import { useTranslation } from 'react-i18next';

export interface LineContextBarProps {
  /** Rincian satuan, mis. `1 × Rp 45.000` — jawaban atas "kok totalnya segitu?". */
  readonly unitLabel: string;
  readonly qty: number;
  /**
   * Batas bawah jumlah — biasanya 1, tapi **sebanyak yang sudah terkirim** bila baris ini sudah
   * sebagian sampai ke dapur. Yang sudah di wajan tak bisa ditarik pulang lewat tombol `−`.
   */
  readonly minQty?: number;
  /**
   * ⚠️ Alasan kenapa penyuntingan dikunci — mis. bill sudah dihitung di server. `undefined` = bebas.
   *
   * ⛔ Sengaja berupa KALIMAT, bukan boolean. Tombol yang mati tanpa penjelasan akan diketuk
   * berulang kali, lalu dilaporkan sebagai "aplikasinya nge-hang".
   */
  readonly lockedReason?: string;
  /**
   * ⛔ `false` ⇒ tombol Hapus TIDAK dipasang — baris ini sudah sebagian sampai ke dapur.
   *
   * ⚠️ Sengaja tak dipasang, bukan dipasang lalu diam-diam tak berbuat apa-apa. Tombol yang
   * ditekan tanpa akibat adalah cara tercepat membuat kasir menekannya lima kali lalu melapor
   * bahwa aplikasinya rusak.
   */
  readonly removable?: boolean;
  readonly onIncrement: () => void;
  readonly onDecrement: () => void;
  readonly onRemove: () => void;
}

export function LineContextBar(p: LineContextBarProps) {
  const { t } = useTranslation();

  return (
    <div className="line-context" data-testid="line-context" aria-label={t('cashier.line.title')}>
      {p.lockedReason === undefined ? (
        <>
          <span className="line-context-unit" data-testid="line-context-unit">{p.unitLabel}</span>
          <div className="line-context-actions">
            <button
              type="button"
              data-testid="line-decrement"
              aria-label={t('cashier.line.decrement')}
              // ⛔ Berhenti di batas bawah (1, atau sebanyak yang sudah terkirim). Menghapus lewat
              //    tombol jumlah adalah penghapusan yang tak diniatkan.
              disabled={p.qty <= (p.minQty ?? 1)}
              onClick={p.onDecrement}
            >
              −
            </button>
            <span className="line-context-qty" data-testid="line-context-qty">{p.qty}</span>
            <button
              type="button"
              data-testid="line-increment"
              aria-label={t('cashier.line.increment')}
              onClick={p.onIncrement}
            >
              +
            </button>
            {p.removable !== false && (
              <button
                type="button"
                className="line-context-remove"
                data-testid="line-remove"
                onClick={p.onRemove}
              >
                {t('cashier.line.remove')}
              </button>
            )}
          </div>
        </>
      ) : (
        <p className="line-context-locked" data-testid="line-context-locked">{p.lockedReason}</p>
      )}
    </div>
  );
}
